import { effect, inject, Injectable, untracked } from '@angular/core';

import { NEW_MATCH_PAGE_SIZE } from '../constants/new-match-notification.constants';
import { getSectionDates, getSectionForDate, getTabsForNotification, mapSectionToDateFilter, mapTabToFilter } from '../helpers/notification-new-match.helper';
import { AppointmentStatus } from '../models/appointment-status';
import { NotificationTabs } from '../models/notification-tabs';
import { allNotificationItems, allTabsNotificationItems, appointmentNotifications, closestPendingAppointmentNotification, customerNotifications, favoriteListingNotifications, filteredNotificationItems, groupedNotifications, isDefaultSorting, isUnreadOnly, listingActivityNotifications, messageNotifications, miscellaneousNotifications, newMatchGroups, NewMatchSectionGroupType, newMatchSections, notificationExpandCounts, notificationsLoaded, savedSearchNotifications, selectedTab } from '../store/services/notification.store';
import { NotificationsApiService } from '../store/services/notifications-api.service';
import { NotificationsGroupingService } from './notification-grouping.service';
import { NotificationsMappingService } from './notifications-mapping.service';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private readonly notificationsGroupingService = inject(NotificationsGroupingService);
    private readonly notificationsMappingService = inject(NotificationsMappingService);
    private readonly notificationsApiService = inject(NotificationsApiService);

    private appointmentUpdateTimeout: ReturnType<typeof setTimeout> | undefined;

    constructor() {
        effect(() => {
            clearTimeout(this.appointmentUpdateTimeout);

            const closestPendingAppointment = closestPendingAppointmentNotification();

            if (closestPendingAppointment == null) {
                return;
            }

            const delay = new Date((closestPendingAppointment.appointment?.startDateTime as string).replace('Z', '')).getTime() - Date.now();

            if (delay > 0) {
                this.appointmentUpdateTimeout = setTimeout(() => {
                    this.updateAppointmentStatus(closestPendingAppointment.entityId, AppointmentStatus.Declined);
                }, delay);
            }
        });

        effect(() => {
            const newNotifications = [
                ...messageNotifications().map(notification =>
                    this.notificationsMappingService.mapCommentNotification(notification)
                ),
                ...appointmentNotifications().map(notification =>
                    this.notificationsMappingService.mapAppointmentNotification(notification)
                ),
                ...customerNotifications().map(notification =>
                    this.notificationsMappingService.mapCommonNotification(notification)
                ),
                ...listingActivityNotifications().map(notification =>
                    this.notificationsMappingService.mapCommonNotification(notification)
                ),
                ...newMatchGroups()[selectedTab()].map(notification => {
                    const section = getSectionForDate(notification.createDate);
                    const totalCount = untracked(() => newMatchSections()[selectedTab()][section].totalCount);

                    return this.notificationsMappingService.mapNewMatchesNotification(notification, totalCount);
                }),
                ...savedSearchNotifications().map(notification =>
                    this.notificationsMappingService.mapCommonNotification(notification)
                ),
                ...miscellaneousNotifications().map(notification =>
                    this.notificationsMappingService.mapCommonNotification(notification)
                ),
                ...favoriteListingNotifications().map(notification =>
                    this.notificationsMappingService.mapFavoriteListingNotification(notification)
                )
            ];

            const currentNotifications = untracked(() => allNotificationItems());

            allNotificationItems.set(newNotifications.map(n => {
                const currentNotification = currentNotifications.find(nf => nf.id === n.id && nf.userId === n.userId);

                return currentNotification == null
                    ? n
                    : {
                        ...n,
                        isViewed: currentNotification.isViewed
                    };
            }));
        });

        effect(() => {
            groupedNotifications.set(this.notificationsGroupingService.groupNotifications(
                filteredNotificationItems(),
                isDefaultSorting()
            ));
        });

        effect(() => {
            const unviewedOnly = isUnreadOnly();

            if (!notificationsLoaded()) {
                return;
            }

            const sections = untracked(() => newMatchSections());
            const maxCurrentPage = Math.max(2, ...Object.values(sections).flatMap(tab => Object.values(tab)).map(s => s.currentPage));
            const pageSize = maxCurrentPage * NEW_MATCH_PAGE_SIZE;

            this.notificationsApiService.loadInitialNewMatchNotifications(unviewedOnly, pageSize);
        });
    }

    public toggleCollapsed(notificationId: number): void {
        const tab = selectedTab();

        notificationExpandCounts.set({
            ...notificationExpandCounts(),
            [tab]: {
                ...notificationExpandCounts()[tab],
                [notificationId]: (notificationExpandCounts()[tab][notificationId] ?? 0) + 1
            }
        });

        const item = allNotificationItems().find(n => n.id === notificationId);

        if (item?.isNewMatch) {
            const section = getSectionForDate(item.notificationDate);

            this.notificationsApiService.loadMoreNewMatchNotifications(section);
        }
    }

    public resetCollapsed(): void {
        notificationExpandCounts.set({
            [NotificationTabs.All]: {},
            [NotificationTabs.Agent]: {},
            [NotificationTabs.Customer]: {}
        });
    }

    public loadUnviewedNotificationsCount(): void {
        this.notificationsApiService.loadUnviewedNotificationsCount();
    }

    public viewUnviewNotification(notificationId: number, isGroup: boolean, shouldPreventUnview?: boolean): void {
        if (shouldPreventUnview && allNotificationItems().find(n => n.id === notificationId)?.isViewed) {
            return;
        }

        const item = allNotificationItems().find(n => n.id === notificationId);

        if (item?.isNewMatch) {
            this.viewNewMatchNotification(notificationId, item.isViewed ?? false, isGroup);

            return;
        }

        if (!isGroup) {
            this.viewUnviewNotifications([notificationId]);

            return;
        }

        const group = groupedNotifications()
            .flatMap(m => m.items)
            .filter(i => (i?.childItems?.length ?? 0) > 0)
            .find(i => i.id === notificationId);

        const isGroupViewed = group?.childItems?.every(n => n.isViewed);

        const notificationIds = group?.childItems
            ?.filter(n => isGroupViewed || !n.isViewed)
            ?.flatMap(child => [child.id]) ?? [notificationId];

        this.viewUnviewNotifications(notificationIds);
    }

    public markAllNotificationsAsRead(): void {
        this.notificationsApiService.viewNewMatchNotificationsByDateSection(null, null, true, 'ALL');

        const allTabs = [NotificationTabs.All, NotificationTabs.Agent, NotificationTabs.Customer];

        newMatchGroups.update(existing => {
            const updated = { ...existing };

            allTabs.forEach(tab => {
                updated[tab] = existing[tab].map(g => ({ ...g, isViewed: true }));
            });

            return updated;
        });

        newMatchSections.update(existing => {
            const updated = { ...existing };

            allTabs.forEach(tab => {
                const updatedTab = { ...existing[tab] };

                [NewMatchSectionGroupType.Today, NewMatchSectionGroupType.Yesterday, NewMatchSectionGroupType.Older].forEach(section => {
                    updatedTab[section] = {
                        ...existing[tab][section],
                        prefetchedGroups: existing[tab][section].prefetchedGroups.map(g => ({ ...g, isViewed: true })),
                        totalUnviewedCount: 0,
                        needsReload: isUnreadOnly()
                    };
                });

                updated[tab] = updatedTab;
            });

            return updated;
        });

        allNotificationItems.set(allNotificationItems().map(n => n.isNewMatch ? { ...n, isViewed: true } : n));

        const otherIds = allTabsNotificationItems()
            .filter(n => !n.isViewed && !n.isNewMatch)
            .map(n => n.id);

        if (otherIds.length > 0) {
            this.viewUnviewNotifications(otherIds);
        }
    }

    public viewUnviewNotifications(notificationIds: number[]): void {
        this.notificationsApiService.viewNotifications(notificationIds);
    }

    public removeCustomerNotifications(customerId: number): void {
        allNotificationItems.set(allNotificationItems().filter(n => n.customerId !== customerId));
    }

    public removeListingNotifications(listingIds: number[], customerId: number): void {
        allNotificationItems.set(
            allNotificationItems().filter(n => !listingIds.includes(n.listingId!) || n.customerId !== customerId)
        );
    }

    public removeEntityNotifications(entityId: number, customerId: number): void {
        allNotificationItems.set(
            allNotificationItems().filter(n => n.entityId !== entityId || n.customerId !== customerId)
        );
    }

    public updateAppointmentStatus(appointmentId: number, agentStatus: string): void {
        // TODO: replace agentStatus string with AppointmentStatus type and use AppointmentStatusService.mapToGraphql() once available
        appointmentNotifications.set(appointmentNotifications().map(a => {
            if (a.entityId !== appointmentId || a.appointment == null) {
                return a;
            }

            return {
                ...a,
                appointment: {
                    ...a.appointment,
                    agentStatus: agentStatus as typeof a.appointment.agentStatus,
                }
            };
        }));
    }

    private viewNewMatchNotification(notificationId: number, currentlyViewed: boolean, isGroup: boolean): void {
        const allTabs = [NotificationTabs.All, NotificationTabs.Agent, NotificationTabs.Customer];
        const group = newMatchGroups()[selectedTab()].find(g => g.notificationId === notificationId);

        if (group == null) {
            return;
        }

        const view = !currentlyViewed;
        const section = getSectionForDate(group.createDate);
        const markForReload = view && isUnreadOnly();

        if (isGroup) {
            this.viewNewMatchGroupNotification(section, view, allTabs, markForReload);
        } else {
            this.notificationsApiService.viewNewMatchNotifications(group.savedSearchId, group.createDate, view);

            newMatchGroups.update(existing => {
                const updated = { ...existing };

                allTabs.forEach(tab => {
                    updated[tab] = existing[tab].map(g => g.notificationId !== notificationId ? g : {
                        ...g,
                        isViewed: view
                    });
                });

                return updated;
            });

            allNotificationItems.set(allNotificationItems().map(n => n.id !== notificationId ? n : {
                ...n,
                isViewed: view
            }));

            const unviewedDelta = view ? -1 : 1;
            const relevantTabs = getTabsForNotification(group);

            newMatchSections.update(existing => {
                const updated = { ...existing };

                relevantTabs.forEach(tab => {
                    updated[tab] = {
                        ...existing[tab],
                        [section]: {
                            ...existing[tab][section],
                            totalCount: isUnreadOnly() ? existing[tab][section].totalCount + unviewedDelta : existing[tab][section].totalCount,
                            totalUnviewedCount: existing[tab][section].totalUnviewedCount + unviewedDelta,
                            needsReload: markForReload || existing[tab][section].needsReload,
                            prefetchedGroups: existing[tab][section].prefetchedGroups.map(g =>
                                g.notificationId === notificationId ? { ...g, isViewed: view } : g
                            )
                        }
                    };
                });

                return updated;
            });

            if (isUnreadOnly()) {
                relevantTabs.forEach(tab => {
                    const state = newMatchSections()[tab][section];
                    const hasViewedAllPrefetched =
                        newMatchGroups()[tab].filter(g => getSectionForDate(g.createDate) === section && !g.isViewed).length <=
                        (state.currentPage - 1) * NEW_MATCH_PAGE_SIZE;

                    if (hasViewedAllPrefetched) {
                        this.notificationsApiService.fetchNewMatchSection(
                            section,
                            (state.currentPage + 1) * NEW_MATCH_PAGE_SIZE,
                            1,
                            mapSectionToDateFilter(section),
                            true,
                            mapTabToFilter(tab),
                            state.currentPage * NEW_MATCH_PAGE_SIZE
                        );
                    }
                });
            }
        }
    }

    private viewNewMatchGroupNotification(
        section: NewMatchSectionGroupType, view: boolean, allTabs: NotificationTabs[], markForReload: boolean): void {
        const currentTab = selectedTab();
        const { dateFrom, dateTo } = getSectionDates(section);

        this.notificationsApiService.viewNewMatchNotificationsByDateSection(dateFrom, dateTo, view, mapTabToFilter(currentTab));

        if (view && currentTab !== NotificationTabs.All) {
            newMatchGroups.update(existing => ({
                ...existing,
                [currentTab]: existing[currentTab].map(g => getSectionForDate(g.createDate) === section ? { ...g, isViewed: true } : g
                )
            }));

            allNotificationItems.set(allNotificationItems().map(n => n.isNewMatch && getSectionForDate(n.notificationDate) === section
                ? { ...n, isViewed: true }
                : n
            ));

            newMatchSections.update(existing => ({
                ...existing,
                [currentTab]: {
                    ...existing[currentTab],
                    [section]: {
                        ...existing[currentTab][section],
                        totalUnviewedCount: view ? 0 : existing[currentTab][section].totalCount,
                        prefetchedGroups: existing[currentTab][section].prefetchedGroups.map(g => ({ ...g, isViewed: view }))
                    }
                }
            }));

            const allTabState = newMatchSections()[NotificationTabs.All][section];
            const targetVisibleCount = (allTabState.currentPage + 1) * NEW_MATCH_PAGE_SIZE;

            this.notificationsApiService.fetchNewMatchSection(
                section,
                targetVisibleCount * 2,
                1,
                mapSectionToDateFilter(section),
                isUnreadOnly(),
                mapTabToFilter(NotificationTabs.All),
                targetVisibleCount
            );
        } else {
            newMatchGroups.update(existing => {
                const updated = { ...existing };

                allTabs.forEach(tab => {
                    updated[tab] = existing[tab].map(g => getSectionForDate(g.createDate) === section ? { ...g, isViewed: view } : g
                    );
                });

                return updated;
            });

            allNotificationItems.set(allNotificationItems().map(n => n.isNewMatch && getSectionForDate(n.notificationDate) === section
                ? { ...n, isViewed: view }
                : n
            ));

            newMatchSections.update(existing => {
                const updated = { ...existing };

                allTabs.forEach(tab => {
                    updated[tab] = {
                        ...existing[tab],
                        [section]: {
                            ...existing[tab][section],
                            totalUnviewedCount: view ? 0 : existing[tab][section].totalCount,
                            needsReload: markForReload || existing[tab][section].needsReload,
                            prefetchedGroups: existing[tab][section].prefetchedGroups.map(g => ({ ...g, isViewed: view }))
                        }
                    };
                });

                return updated;
            });
        }
    }
}
